import React, { useContext } from 'react';
import {
    showNiceDate,
    WidgetPanelDetailProps,
} from 'widgets/supportVisualisation/Content/Widgets/WidgetPanel';
import {
    FillsChartType,
    FillsGraphConfiguration,
    OrderFillsConfig,
} from 'widgets/fillsGraph/store/configuration/FillsGraphConfiguration';
import { FillsGraphDataState } from 'widgets/fillsGraph/store/data/FillsGraphDataState';
import {
    deriveAverageExecutionPrice,
    deriveLimitPrice,
    deriveNormalisedBenchmarkPrice,
    deriveOrderFillBucketFillPrice,
    derivePrice,
    deriveVwap,
    getFillsGraphData,
    OrderFill,
} from 'widgets/fillsGraph/fillsGraphData';
import JsonPanel from 'utils/components/JsonPanel/JsonPanel';
import { getYDomain } from 'widgets/fillsGraph/orderFills/OrderFillsChart';
import { getFillsGraphPortfolioData } from 'widgets/fillsGraph/fillsGraphPortfolioData';
import {
    selectPortfolioSelectedOrders,
    selectPortfolioSelection,
    selectPortfolioStreamingOrderIds,
    selectStreamedOrSelectedOrders,
} from 'portfolio/store/selectors';
import { selectStreamingOrders } from 'app/store/orders/selectors';
import AppContext from 'app/AppContext';
import { WidgetPanelGrid } from 'widgets/supportVisualisation/Content/Widgets/WidgetPanelGrid';
import { createFillsGraphRequest } from 'widgets/fillsGraph/store/data/FillsGraphStreamRequest';
import { selectAppSettings } from 'app/store/appSettings/selectors';
import { PortfolioSelectionWrapper } from 'portfolio/PortfolioSelectionWrapper';
import { getFillsGraphPlData } from 'widgets/fillsGraph/pl/fillsGraphPlData';
import { ColDef } from 'ag-grid-community';
import {
    selectIndigoMarketDataEnabled,
    selectMarketBarDataBars,
    selectRefData,
} from 'marketData/store/selectors';
import { getExternalMarketBarData } from 'marketData/hooks';
import { useSymbolInBloombergSymbology } from 'symbols/symbolInfo/hooks';

export interface WidgetPanelFillsGraphProps extends WidgetPanelDetailProps {
    mode:
        | 'data'
        | 'stream'
        | 'timeSeries'
        | 'pl'
        | 'bucketed'
        | 'breaks'
        | 'range labels'
        | 'strategy parameter data'
        | 'request';
}

export default function WidgetPanelFillsGraph(
    props: WidgetPanelFillsGraphProps
) {
    const {
        state,
        widgetState,
        selection,
        mode,
        widget: { id: widgetKey },
    } = props;

    const data = widgetState.stream.data as FillsGraphDataState;
    const config = widgetState.config as FillsGraphConfiguration;
    const {
        services: { portfolioService },
    } = useContext(AppContext);

    const bbSymbol = useSymbolInBloombergSymbology(
        selection.selectedSymbol,
        selection.symbology
    );

    if (!data || !config) {
        return <></>;
    }

    const activeInputUsesOrdersStream =
        !!portfolioService.selectPortfolioActiveInput(state.state)
            ?.usesOrdersStream;
    const selectedOrders = selectPortfolioSelectedOrders(state.state);
    const streamingOrders = selectStreamingOrders(state.state);
    const portfolioStreamingOrders = selectStreamedOrSelectedOrders(
        streamingOrders,
        selectedOrders,
        activeInputUsesOrdersStream
    );

    const indigoMarketDataEnabled = selectIndigoMarketDataEnabled(state.state);

    const bars = bbSymbol
        ? selectMarketBarDataBars(state.state, bbSymbol, 1)
        : undefined;
    const refData = selectRefData(state.state, bbSymbol);
    const dapiPriceData = getExternalMarketBarData(
        indigoMarketDataEnabled,
        bars,
        refData
    );

    const indexBars = bbSymbol
        ? selectMarketBarDataBars(state.state, config.general.fallbackIndex, 1)
        : undefined;
    const indexRefData = selectRefData(
        state.state,
        config.general.fallbackIndex
    );
    const indexDapiPriceData = getExternalMarketBarData(
        indigoMarketDataEnabled,
        indexBars,
        indexRefData
    );

    const fillsGraphPortfolioData = getFillsGraphPortfolioData(
        selection.selectedPosition,
        portfolioStreamingOrders,
        selection.selectedSymbolQuantity
    );
    const fillsGraphData =
        fillsGraphPortfolioData &&
        getFillsGraphData(
            data,
            config,
            fillsGraphPortfolioData.side,
            dapiPriceData,
            refData,
            indexDapiPriceData,
            indexRefData,
            []
        );

    const plData = fillsGraphData ? getFillsGraphPlData(fillsGraphData) : [];

    const orderLimitPrice =
        fillsGraphData?.strategyParametersLimitPriceAvailable
            ? fillsGraphPortfolioData?.orderLimitPrice
            : undefined;

    const yDomain =
        fillsGraphData &&
        buildYDomain(
            fillsGraphData.data,
            fillsGraphData.snapPrice,
            fillsGraphData.closePrice,
            fillsGraphData.open,
            config[FillsChartType.OrderFills],
            orderLimitPrice,
            fillsGraphData.strategyParametersLimitPriceAvailable
        );

    const streamingOrderIds = selectPortfolioStreamingOrderIds(state!.state);
    const portfolioSelection = selectPortfolioSelection(state!.state);
    const appSettings = selectAppSettings(state!.state);
    const request = portfolioSelection
        ? createFillsGraphRequest(
              streamingOrderIds,
              new PortfolioSelectionWrapper(portfolioSelection),
              widgetKey,
              appSettings,
              state.state
          )
        : undefined;

    if (fillsGraphData) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (fillsGraphData as any).yDomain = yDomain;

        showNiceDate(fillsGraphData, 'timeCompleted');
        for (const b of fillsGraphData.bucketedData) {
            showNiceDate(b, 'startTime');
            showNiceDate(b, 'midTime');
            showNiceDate(b, 'endTime');
            if (b.candle) {
                const bb = b as {
                    open?: number;
                    high?: number;
                    low?: number;
                    close?: number;
                    candle?: unknown;
                };
                bb.open = b.candle.open;
                bb.high = b.candle.high;
                bb.low = b.candle.low;
                bb.close = b.candle.close;
                delete bb.candle;
            }
        }
        for (const t of fillsGraphData.data) {
            showNiceDate(t, 'time');
        }
        for (const r of fillsGraphData.rangeLabels) {
            showNiceDate(r, 'open');
            showNiceDate(r, 'close');
        }
        for (const b of fillsGraphData.breaksInTrading) {
            showNiceDate(b, 'from');
            showNiceDate(b, 'to');
        }
    }

    for (const b of plData) {
        showNiceDate(b, 'time');
    }

    if (!fillsGraphData) {
        return <div className="widget-panel widget-panel-fills">No data</div>;
    }

    const json = { fillsGraphPortfolioData, fillsGraphData: fillsGraphData };

    switch (mode) {
        case 'data':
            return (
                <div className="widget-panel widget-panel-fills">
                    <div className="title">Fills Graph Data</div>
                    <JsonPanel data={json} />
                </div>
            );
        case 'pl':
            return (
                <WidgetPanelGrid
                    key="stream"
                    data={plData}
                    getRowDataId={(d) => d.time.toString()}
                    csvFileName={`${widgetKey}-pl-data`}
                />
            );
        case 'request':
            return (
                <div className="widget-panel widget-panel-fills">
                    <div className="title">Fills Graph Request</div>
                    <JsonPanel data={request} />
                </div>
            );
        case 'stream':
            return (
                <WidgetPanelGrid
                    key="stream"
                    data={data.timeSeries}
                    getRowDataId={(d) => d._i.toString()}
                    csvFileName={`${widgetKey}-stream`}
                    colDefs={getStreamColDefs()}
                />
            );
        case 'timeSeries':
            return (
                <WidgetPanelGrid
                    key="timeSeries"
                    data={fillsGraphData!.data}
                    getRowDataId={(d) => d.time.toString()}
                    csvFileName={`${widgetKey}-timeSeries`}
                />
            );
        case 'bucketed':
            return (
                <WidgetPanelGrid
                    key="buckets"
                    data={fillsGraphData!.bucketedData}
                    getRowDataId={(d) => d.startTime.toString()}
                    csvFileName={`${widgetKey}-buckets`}
                />
            );
        case 'range labels':
            return (
                <WidgetPanelGrid
                    key="rangeLabels"
                    data={fillsGraphData!.rangeLabels}
                    getRowDataId={(d) => d.open.toString()}
                    csvFileName={`${widgetKey}-rangeLabels`}
                />
            );
        case 'strategy parameter data':
            return (
                <WidgetPanelGrid
                    key="strategyParameterData"
                    data={fillsGraphData!.strategyParameterData ?? []}
                    getRowDataId={(d) => d.time.toString()}
                    csvFileName={`${widgetKey}-strategyParameterData`}
                />
            );
        case 'breaks':
            return (
                <WidgetPanelGrid
                    key="breaks"
                    data={fillsGraphData!.breaksInTrading ?? []}
                    getRowDataId={(d) => d.from.toString()}
                    csvFileName={`${widgetKey}-breaks`}
                />
            );
        default:
            return (
                <div className="widget-panel widget-panel-fills">
                    Unrecognised mode {mode}
                </div>
            );
    }
}

function buildYDomain(
    data: OrderFill[],
    snapPrice: number | undefined,
    closePrice: number | undefined,
    open: number | undefined,
    config: OrderFillsConfig,
    orderLimitPrice: number | undefined,
    strategyParametersLimitPriceAvailable: boolean
) {
    type LimitItem = {
        time: number;
        displayTime: Date;
        value: number;
    };
    type Limit = {
        min?: LimitItem;
        max?: LimitItem;
    };

    const limits = {
        price: {} as Limit,
        vwap: {} as Limit,
        averageExecutionPrice: {} as Limit,
        orderFillBucketFillPrice: {} as Limit,
        limitPrice: {} as Limit,
        normalisedBenchmarkPrice: {} as Limit,
    };

    for (const item of data) {
        const price = derivePrice(item);
        applyToLimit(limits.price, item.time, price);

        const vwap = deriveVwap(item);
        applyToLimit(limits.vwap, item.time, vwap);

        const averageExecutionPrice = deriveAverageExecutionPrice(item);
        applyToLimit(
            limits.averageExecutionPrice,
            item.time,
            averageExecutionPrice
        );

        const orderFillBucketFillPrice = deriveOrderFillBucketFillPrice(item);
        if (orderFillBucketFillPrice !== 0) {
            applyToLimit(
                limits.orderFillBucketFillPrice,
                item.time,
                orderFillBucketFillPrice
            );
        }

        const limitPrice = deriveLimitPrice(item);
        applyToLimit(limits.limitPrice, item.time, limitPrice);

        const normalisedBenchmarkPrice = deriveNormalisedBenchmarkPrice(item);
        applyToLimit(
            limits.normalisedBenchmarkPrice,
            item.time,
            normalisedBenchmarkPrice
        );
    }

    const limitPriceAccessor: (d: OrderFill) => number | undefined =
        strategyParametersLimitPriceAvailable
            ? deriveLimitPrice
            : () => orderLimitPrice;

    return {
        yDomain: getYDomain(
            data,
            snapPrice,
            closePrice,
            open,
            config,
            limitPriceAccessor
        ),
        limits,
        snapPrice,
        closePrice,
        config,
    };

    function applyToLimit(
        limit: Limit,
        time: number,
        value: number | undefined
    ) {
        if (value === undefined) {
            return;
        }
        const limitItem = {
            time: time,
            displayTime: new Date(time),
            value,
        };

        if (!limit.min || value < limit.min.value) {
            limit.min = limitItem;
        }
        if (!limit.max || value > limit.max.value) {
            limit.max = limitItem;
        }
    }
}

export function addFillsGraphPanes(
    detailProps: WidgetPanelDetailProps,
    panes: { menuItem: string; render: () => JSX.Element }[]
) {
    const render = () => (
        <WidgetPanelFillsGraph {...detailProps} mode={'data'} />
    );
    panes.push({
        menuItem: 'Data',
        render,
    });

    const renderStream = () => (
        <WidgetPanelFillsGraph {...detailProps} mode={'stream'} />
    );
    panes.push({
        menuItem: 'Stream',
        render: renderStream,
    });

    const renderTs = () => (
        <WidgetPanelFillsGraph {...detailProps} mode={'timeSeries'} />
    );
    panes.push({
        menuItem: 'Time Series',
        render: renderTs,
    });

    const renderPl = () => (
        <WidgetPanelFillsGraph {...detailProps} mode={'pl'} />
    );
    panes.push({
        menuItem: 'P&L',
        render: renderPl,
    });

    const renderBuck = () => (
        <WidgetPanelFillsGraph {...detailProps} mode={'bucketed'} />
    );
    panes.push({
        menuItem: 'Buckets',
        render: renderBuck,
    });

    const renderBr = () => (
        <WidgetPanelFillsGraph {...detailProps} mode={'breaks'} />
    );
    panes.push({
        menuItem: 'Breaks',
        render: renderBr,
    });

    const renderRl = () => (
        <WidgetPanelFillsGraph {...detailProps} mode={'range labels'} />
    );
    panes.push({
        menuItem: 'Range Labels',
        render: renderRl,
    });

    const renderSp = () => (
        <WidgetPanelFillsGraph
            {...detailProps}
            mode={'strategy parameter data'}
        />
    );
    panes.push({
        menuItem: 'Algo Params',
        render: renderSp,
    });

    const renderReq = () => (
        <WidgetPanelFillsGraph {...detailProps} mode={'request'} />
    );
    panes.push({
        menuItem: 'Request',
        render: renderReq,
    });
}

function getStreamColDefs(): ColDef[] {
    return [
        '_i',
        'd',
        'pc',
        'pn',
        's',
        'index_c',
        'index_bps',
        'o',
        'h',
        'l',
        'c',
        'w',
        'bAvg',
        'sAvg',
        'ch',
        'ich',
        'rch',
        'ec',
        'si',
        'v',
        'vp',
        'bt',
        'bp',
        'st',
        'sp',
        'tPL',
        'rPL',
        'urPL',
    ].map((key) => ({
        field: key,
        key,
        width: 120,
        resizable: true,
    }));
}
